import {
	GetApplyFilterFn,
	getGridBooleanOperators,
	getGridDateOperators,
	getGridNumericOperators,
	getGridSingleSelectOperators,
	getGridStringOperators,
	GridColumnTypes,
	GridFilterOperator,
	GridValidRowModel,
	getGridStringQuickFilterFn,
	GetApplyQuickFilterFn,
	GridRowId,
} from "@mui/x-data-grid-premium";

type GetApplyFilterFnParams<RowDataType extends GridValidRowModel> = Parameters<NonNullable<ReturnType<GetApplyFilterFn<RowDataType>>>>;

type ExtendedParams<RowDataType extends GridValidRowModel, ValueType = unknown> = [
	...GetApplyFilterFnParams<RowDataType>,
	NonNullable<ReturnType<GetApplyFilterFn<RowDataType, ValueType>>>
];

/**
 * Represents a custom filter function that will run before any MUI filter.
 *
 * @template RowDataType The type of data in the row.
 * @template ValueType The type of value used for filtering.
 *
 * @returns A boolean value indicating whether the filter condition is met, or undefined if the filter cannot be applied.
 * @example Return true --> Force pass all the filters
 * @example Return false --> Force not to pass any filters
 * @example Return undefined --> Apply mui's filters normally
 */
export type TWrapperFilterFunction<RowDataType extends GridValidRowModel = { _id: GridRowId }, ValueType = unknown> = (
	...args: ExtendedParams<RowDataType, ValueType>
) => boolean | undefined;

const createFilterOperatorWrapper =
	<RowDataType extends GridValidRowModel, ValueType>(
		originalGetFilterFn: GetApplyFilterFn<RowDataType, ValueType>,
		customFilterFunctionallity: TWrapperFilterFunction<RowDataType, ValueType>
	): GetApplyFilterFn<RowDataType, ValueType> =>
	(filterItem, column): ReturnType<GetApplyFilterFn<RowDataType, ValueType>> => {
		const filterFn = originalGetFilterFn(filterItem, column);
		if (!filterFn) {
			return null;
		}
		return (value, row, col, apiRef) => {
			const result = customFilterFunctionallity(value, row, col, apiRef, filterFn);
			if (result || result === false) {
				return result;
			}

			return filterFn(value, row, col, apiRef);
		};
	};

export const wrapFilterOperators = <RowDataType extends GridValidRowModel>(preFilterFn: TWrapperFilterFunction<RowDataType>) => {
	const wrappingFn = (operator: GridFilterOperator) => ({
		...operator,
		getApplyFilterFn: createFilterOperatorWrapper(operator.getApplyFilterFn, preFilterFn),
	});
	return {
		date: getGridDateOperators().map(wrappingFn),
		dateTime: getGridDateOperators().map(wrappingFn),
		string: getGridStringOperators().map(wrappingFn),
		boolean: getGridBooleanOperators().map(wrappingFn),
		number: getGridNumericOperators().map(wrappingFn),
		singleSelect: getGridSingleSelectOperators().map(wrappingFn),
	} as Partial<Record<keyof GridColumnTypes, GridFilterOperator<RowDataType>[]>>;
};

export const wrapQuickFilter =
	<RowDataType extends GridValidRowModel>(preFilterFn: TWrapperFilterFunction<RowDataType>): GetApplyQuickFilterFn<RowDataType> =>
	(outerValue, outerColDef, outerApiRef) => {
		const originalQuickFilterFn = getGridStringQuickFilterFn(outerValue, outerColDef, outerApiRef);
		if (!originalQuickFilterFn) {
			return originalQuickFilterFn;
		}

		return (value, row, column, apiRef) => {
			const prefilterResult = preFilterFn(value, row, column, apiRef, originalQuickFilterFn);
			if (prefilterResult || prefilterResult === false) {
				return prefilterResult;
			}

			return originalQuickFilterFn(value, row, column, apiRef);
		};
	};
